import React, { useState, useEffect } from 'react';
import { Dialog, IconButton, Typography, Icon, Button, Select, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useStyles from './ShareModal.styles';
import { replace, first, get, last } from 'global/lib/lodash';
import { Alert } from '@mui/material';
import clsx from 'clsx';
import { copyToClipboard, addNewLinesToText, checkCanShare } from 'global/utils';
import SnackAlerts from 'global/components/snack-alerts';
import { getTradeName } from '../../../global/components/trade-select';
import { CenterLoader } from '../../../global/styles';
import sendData from 'redux/actions/sendData';

export default (props) => {
  const [error, setError] = useState();
  const [alerts, setAlerts] = useState();
  const [canShare, setCanShare] = useState(false);
  const [selectOpts, setSelectOpts] = useState([]);
  const [selected, setSelected] = useState();
  const [state, setState] = useState({
    loading: false,
    links: []
  })

  const classes = useStyles();

  const title = props.title || 'Share PDF Link';

  const handleClose = () => {
    props.onClose();
  };

  const clearAlerts = () => {
    setAlerts();
  };

  const getLink = (lnk = selected) => {
    if (props.link) { return props.link }

    const isAll = lnk == 'all';
    const link = get(state, `links`, []).find(l => isAll ? get(l, 'customProperties.tradeType') == 'all' : get(l, 'customProperties.tradeTypeId') == lnk);
    return get(link, 'full7DayUrl');
  }

  const copyLink = () => {
    clearAlerts();
    const link = getLink();
    copyToClipboard(link);
    setAlerts({
      message: 'Link copied to clipboard.',
    });
  };

  const openLink = () => {
    window.open(getLink(), '_blank');
  };

  const copyToEmail = () => {
    //this is all necessary because the ? and & get stripped from the body
    const repl = {
      '\\?': '%3F',
      '\\&': '%26',
    };

    const emailText = `You've been assigned a new task, please review the pdf here: `;
    const fullText = props.emailText || 
`${emailText}${props.link ? '' : getLink()}

Thanks,
`;
    let txt = addNewLinesToText(fullText, '%0D%0A%0D%0A');
    Object.keys(repl).map((k) => {
      txt = replace(txt, new RegExp(k, 'g'), repl[k]);
    });
    const subject = props.isScorePdf ? `Assessment Score PDF` : title;
    let url = `mailto:user@example.com?subject=${subject}&body=${txt}`;
    window.open(url, '_blank');
  };

  const share = async () => {
    try {
      await navigator.share({ title, url: getLink() });
    } catch (err) {
      console.warn(err);
    }
  };

  const changeSelected = async e => {
    try {
      const val = e.target.value;
      setSelected(val);

      const hasLink = getLink(val);

      if (!hasLink) {
        setState(s => ({ ...s, loading: true }));

        const opts = [...selectOpts];
        opts.shift()

        const relationships = {
          tasks: {
            data: props.tasks.map(t => ({ id: t.id, type: 'tasks' })),
          },
          tradeTypes: {
            data: opts.filter(f => f.id == val).map(t => ({ id: t.id, type: 'trade-types' }))
          }
        }

        const resp = await sendData({
          url: `/messages` + (state.id ? `/${state.id}` : ''),
          method: state.id ? 'patch' : 'post',
          data: {
            data: {
              type: 'messages',
              id: state.id,
              attributes: {
                title: 'New Task', 
                body: 'Test',
                model_type: 'task',
                realm_id: get(last(props.tasks), 'realm.id'),
                status: 1,
                trade_type: val === 'all' ? 'all' : get(selectOpts.find(o => o.id == val), 'name'),
                trade_type_id: val === 'all' ? null : val,
                // separate_by_trades: val !== 'all',
              },
              relationships,
            }
          }
        }, { normalize: { build: ['messages'] }, params: { include: 'media' } });

        const res = last(resp);
        const links = get(res, 'media', []);

        setState(s => ({ ...s, links, id: res.id }))
      }
    } catch (err) {
      setAlerts({ message: err.toString(), severity: 'error' });
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  }

  useEffect(() => {
    if (props.open) {
      const canShare = checkCanShare();
      setCanShare(canShare);

      //#region set the dropdown options
      if (!props.link) {
        const tradeOptions = [];
        let total = 0;
        if (props.tasks) {
          props.tasks.map(t => {
            total++;
            (t.tradeTypes || []).map(trade => {
              const hasTrade = tradeOptions.find(t => t.id == trade.id);
              if (hasTrade) {
                hasTrade.total++;
              } else {
                tradeOptions.push({ ...trade, total: 1 });
              }
            })
          })
        }
        const allOpts = [{ name: 'All Trades', id: 'all', total }, ...tradeOptions];
        setSelectOpts(allOpts);
        changeSelected({ target: { value: first(allOpts).id }});
      }
      //#endregion

      setTimeout(() => {
        if (canShare) {
          try {
            document.getElementById('ShareBtn').click();
          } catch (err) {
            console.warn(err);
          }
        }
      }, 500);
    } else {
      setState(s => ({ ...s, links: [], id: undefined }))
    }
  }, [props.open]);

  return (
    <Dialog className={classes.dialog} disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <div className={classes.container}>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <IconButton className={classes.closeIcon} onClick={handleClose} size="large">
          <Icon>close</Icon>
        </IconButton>
        <div className={classes.titleRow}>
          <Typography variant="h5" className={classes.subtitle}>
            {title}
          </Typography>
        </div>

        <div className={classes.inputContainer}>
          <div className={classes.buttonContainer}>
            {!!state.loading && <CenterLoader size={60} />}
            {!state.loading && <>
              {!props.link && <div className={classes.button}>
                <Select size="small" fullWidth variant="outlined" value={selected} onChange={changeSelected}>
                  {selectOpts.map(s => <MenuItem value={s.id}>{`${getTradeName(s)} (${s.total})`}</MenuItem>)}
                </Select>
              </div>}

              <Button onClick={openLink} variant="outlined" color="secondary" className={classes.button}>
                VIEW PDF
              </Button>

              <Button onClick={copyLink} startIcon={<Icon>filter_none</Icon>} variant="contained" color="secondary" className={classes.button}>
                COPY LINK
              </Button>

              <Button onClick={copyToEmail} startIcon={<Icon>mail</Icon>} variant="contained" color="secondary" className={classes.button}>
                COPY TO EMAIL
              </Button>

              {canShare && (
                <Button onClick={share} startIcon={<Icon>share</Icon>} variant="contained" color="secondary" className={classes.button} id="ShareBtn">
                  SHARE
                </Button>
              )}
            </>}

            <Button onClick={handleClose} variant="contained" startIcon={<Icon>close</Icon>} className={clsx(classes.button, classes.closeBtn)}>
              CLOSE
            </Button>
          </div>
        </div>
      </div>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </Dialog>
  );
};
